import type * as ents from '@attain-apps/entities'

import type { Feature } from './feature'

/** Read model for the future concurrent connector-run launcher. */
export class Selectors {
  /** Whether the run launcher is currently open. */
  public readonly isLauncherOpen
  /** Latest run entries keyed by connector ID. */
  public readonly entries
  /** Returns the latest run entry for one connector. */
  public readonly entry
  /** Whether one connector currently has an active request. */
  public readonly isRunning
  /** Returns the latest failure for one connector. */
  public readonly error
  /** Whether one connector's latest run succeeded. */
  public readonly succeeded
  /** Returns model IDs selected for truncation for one connector. */
  public readonly truncateModelIds

  constructor(feature: Feature) {
    this.isLauncherOpen = feature.state.project(state => state.isLauncherOpen)
    this.entries = feature.state.project(state => state.entries)
    this.entry = feature.createSelectorMethod(
      (connectorId: ents.core.integrations.Connector.Id) =>
        this.entries.project(entries => entries[connectorId]).asSelector,
    )
    this.isRunning = feature.createSelectorMethod(
      (connectorId: ents.core.integrations.Connector.Id) =>
        this.entry(connectorId).project(entry => entry?.loading === true)
          .asSelector,
    )
    this.error = feature.createSelectorMethod(
      (connectorId: ents.core.integrations.Connector.Id) =>
        this.entry(connectorId).project(entry => entry?.error).asSelector,
    )
    this.succeeded = feature.createSelectorMethod(
      (connectorId: ents.core.integrations.Connector.Id) =>
        this.entry(connectorId).project(entry => entry?.data === true)
          .asSelector,
    )
    // NOTE: the lookup is keyed only by validated DMS model IDs, but
    // Object.keys() generalizes those keys to strings.
    this.truncateModelIds = feature.createSelectorMethod(
      (connectorId: ents.core.integrations.Connector.Id) =>
        feature.state
          .project(state => state.truncationSelections[connectorId])
          .project(
            selections =>
              Object.keys(
                selections ?? {},
              ) as ents.core.dms.models.ForRecord.Id[],
          ).asSelector,
    )
  }
}
